import type { Href } from 'expo-router'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useEffect, useRef } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import { Card, Icon, Text, useTheme } from 'react-native-paper'
import { getNavItems } from '@/constants/navigation'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useOrgBySlug } from '@/hooks/useOrgBySlug'
import { useAuth } from '@/lib/auth'
import { signInUrl } from '@/lib/auth-redirect'
import { BREAKPOINTS } from '@/lib/config'
import type { IconType } from '@/lib/types'
import { ButtonLink } from '@/ui/Link'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { OrgHeading } from '@/ui/OrgHeading'
import { Page } from '@/ui/Page'

interface DashboardOptionProps {
    href: Href
    iconName: IconType
    iconColor: string
    iconBackgroundColor: string
    title: string
    description: string
    testID: string
}

function DashboardOption({
    href,
    iconName,
    iconColor,
    iconBackgroundColor,
    title,
    description,
    testID,
}: DashboardOptionProps) {
    const router = useRouter()
    const theme = useTheme()

    return (
        <Card style={styles.dashboardOption} onPress={() => router.navigate(href)} testID={testID}>
            <Card.Content style={styles.dashboardContent}>
                <View style={[styles.iconContainer, { backgroundColor: iconBackgroundColor }]}>
                    <Icon source={iconName} size={24} color={iconColor} />
                </View>

                <View style={styles.textContainer}>
                    <Text variant="titleMedium" style={styles.optionTitle}>
                        {title}
                    </Text>
                    <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                        {description}
                    </Text>
                </View>
                <Icon source="arrow-right" size={20} color={theme.colors.onSurfaceVariant} />
            </Card.Content>
        </Card>
    )
}

interface ErrorPanelProps {
    title: string
    message: string
    actionButton?: React.ReactNode
}

function ErrorPanel({ title, message, actionButton }: ErrorPanelProps) {
    const semanticColors = useSemanticColors()

    return (
        <View style={styles.errorContainer}>
            <View
                style={[
                    styles.errorPanel,
                    {
                        borderColor: semanticColors.errorBorder,
                        backgroundColor: semanticColors.errorLight,
                    },
                ]}
            >
                <Text
                    variant="headlineLarge"
                    style={[styles.errorTitle, { color: semanticColors.errorText }]}
                >
                    {title}
                </Text>

                <Text
                    variant="bodyLarge"
                    style={[styles.errorMessage, { color: semanticColors.errorText }]}
                >
                    {message}
                </Text>

                {actionButton && <View style={styles.buttonContainer}>{actionButton}</View>}
            </View>
        </View>
    )
}

export default function OrgDashboard() {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const router = useRouter()
    const { width } = useWindowDimensions()
    const { isLoggedIn, user } = useAuth({ throwIfAnon: false })
    const { role, hasAccess, isLoading } = useOrgBySlug(params.orgSlug)
    const isRedirectingRef = useRef(false)

    const isDesktop = width >= BREAKPOINTS.lg

    useEffect(() => {
        if (!params.orgSlug || isRedirectingRef.current) {
            return
        }

        if (role === 'workforce') {
            isRedirectingRef.current = true
            router.replace(`/${params.orgSlug}/entry`)
            return
        }

        if (isDesktop && role) {
            isRedirectingRef.current = true
            router.replace(`/${params.orgSlug}/monitor`)
        }
    }, [role, params.orgSlug, router, isDesktop])

    if (isLoading) {
        return (
            <Page headerLess>
                <LoadingIndicator />
            </Page>
        )
    }

    if (!isLoggedIn || !user) {
        return (
            <Page scrollable={false} headerLess>
                <ErrorPanel
                    title="Authentication Required"
                    message="You need to sign in to access this organization."
                    actionButton={
                        <ButtonLink href={signInUrl(`/${params.orgSlug}`)} title="Sign In" />
                    }
                />
            </Page>
        )
    }

    if (!hasAccess) {
        return (
            <Page scrollable={false} headerLess>
                <ErrorPanel
                    title="Access Unavailable"
                    message="You appear to have been logged out or do not have permissions to view this organization."
                    actionButton={
                        <ButtonLink
                            href={signInUrl(`/${params.orgSlug}`)}
                            title="Sign In"
                            mode="contained"
                        />
                    }
                />
            </Page>
        )
    }

    if (!params.orgSlug) {
        return (
            <Page scrollable={false} headerLess>
                <ErrorPanel
                    title="Organization Not Found"
                    message="The organization you're looking for could not be found."
                />
            </Page>
        )
    }

    if (role === 'workforce') {
        return (
            <Page headerLess>
                <LoadingIndicator />
            </Page>
        )
    }

    return (
        <Page headerLess>
            <View style={styles.navigationSection} testID="org-dashboard-page">
                <OrgHeading orgSlug={params.orgSlug} />
                <View style={styles.optionsContainer}>
                    {getNavItems(role).map(option => (
                        <DashboardOption
                            key={option.pathname}
                            href={
                                {
                                    pathname: option.pathname,
                                    params: { orgSlug: params.orgSlug },
                                } as Href
                            }
                            iconName={option.iconName}
                            iconColor={option.iconColor}
                            iconBackgroundColor={option.iconBackgroundColor}
                            title={option.title}
                            description={option.description}
                            testID={option.testID}
                        />
                    ))}
                </View>
            </View>
        </Page>
    )
}

const styles = StyleSheet.create({
    optionsContainer: {
        gap: 12,
        display: 'flex',
        width: '100%',
    },

    errorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        maxWidth: 600,
        width: '100%',
        alignSelf: 'center',
    },
    errorPanel: {
        padding: 24,
        gap: 16,
        borderWidth: 2,
        borderRadius: 8,
        alignItems: 'center',
        width: '100%',
    },
    errorTitle: {
        fontWeight: 'bold',
        textAlign: 'center',
    },
    errorMessage: {
        textAlign: 'center',
    },
    buttonContainer: {
        flexDirection: 'row',
        gap: 12,
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    navigationSection: {
        gap: 16,
    },

    dashboardOption: {
        marginVertical: 4,
        width: '100%',
    },
    dashboardContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        padding: 16,
    },
    iconContainer: {
        width: 48,
        height: 48,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    textContainer: {
        flex: 1,
        gap: 4,
        backgroundColor: 'transparent',
    },
    optionTitle: {
        fontWeight: '600',
    },
})
